import { ChangeDetectionStrategy, Component, forwardRef, Inject, inject, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { CONFIG } from '@nano/core/config';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Account, accountCompareFn, AccountService } from '../shared';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';

@UntilDestroy()
@Component({
  selector: 'app-account-select',
  templateUrl: './account-select.component.html',
  styleUrls: ['./account-select.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AccountSelectComponent), multi: true }],
  standalone: false
})
export class AccountSelectComponent implements ControlValueAccessor, OnInit {
  @Input() formControl: UntypedFormControl;
  @Input() nzSize: 'small' | 'default' | 'large' = 'default';
  @Input() nzPlaceHolder: string;
  @Input() onlyPositiveAmountsSelectable = true;
  @Input() accountNameLookup = false;

  readonly accounts$: Observable<Account[]>;
  readonly visibleAccounts$: Observable<Account[]>;

  value: Account;
  disabled: boolean;
  touched: boolean;
  showIbanInPaymentAccountSelection: boolean;

  accountCompareFn = accountCompareFn;

  onChange = (value: Account): void => {
    this.value = value;
  };
  onTouched = (touched: boolean): void => {
    this.touched = touched;
  };

  private config = inject(CONFIG);

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private accountService: AccountService
  ) {
    this.accounts$ = this.accountService.accounts$;
    this.visibleAccounts$ = this.accounts$.pipe(map((accounts) => accounts.filter((account) => !account?.isViewOnly)));
    this.showIbanInPaymentAccountSelection = this.config.showIbanInPaymentAccountSelection;
  }

  ngOnInit(): void {
    this.accounts$
      .pipe(
        untilDestroyed(this),
        map((accounts) => accounts.filter((account) => this.isAccountSelectable(account)))
      )
      .subscribe((accounts) => {
        if (accounts.length === 1) {
          this.formControl.setValue(accounts[0]);
        }
      });
  }

  writeValue(value: Account): void {
    if (!value?.accountName && this.accountNameLookup && value?.iban && value?.currency) {
      this.accounts$
        .pipe(
          take(1),
          map((accounts) => accounts.filter((account) => account.iban === value.iban && account.currency === value.currency))
        )
        .subscribe((accounts) => {
          value = accounts[0];
          this.formControl.setValue(value);
        });
    }
    this.value = value;
  }

  registerOnChange(onChange: (value: Account) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: (touched: boolean) => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
  }

  isAccountSelectable(account: Account): boolean {
    return !(this.onlyPositiveAmountsSelectable && account.availableFunds <= 0);
  }
}
